/**
 * PRD.305 — the Laboratory Result drawer.
 *
 * Opened by the Result Ready step. Clicking that step never writes a status:
 * it opens this drawer, and only a SUCCESSFUL save creates the canonical
 * `lab_results` row, links its `lab_result_files` artifacts, stamps
 * `result_ready_at` and moves the test to `result_ready`. Closing or cancelling
 * leaves the test exactly where it was.
 *
 * Re-opening a test that already has a result VIEWS that result (and can append
 * files to a draft) rather than creating a duplicate.
 *
 * ── Result content ──
 * A result may carry structured Lab Work FORMS, uploaded report FILES, or
 * both, under one canonical `lab_results` row. The drawer loads the published
 * Lab Work forms assigned to the test's laboratory service, so the technician
 * never searches the global forms catalog:
 *
 *   zero forms     → Upload Report only
 *   one form       → that form presented directly, Upload Report retained
 *   several forms  → pick whichever apply, Upload Report retained
 *
 * Completing every assigned form is never required. Forms owns the response
 * data (`form_instance_values`); Laboratory owns only the result lifecycle.
 *
 * Results always operate on a single `lab_order_tests` row, so a multi-test
 * order gets one result per test.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Drawer } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qLabResults,
  qLabResultFilesForTest,
  qLabResultForms,
  qLabWorkFormsForService,
  qNoOp,
  labResultFormsPresentation,
  canSaveLabResult,
  type LabWorkFormRow,
  type LabResultFormRow,
  canUploadLabResult,
  partitionLabArtifacts,
  isLabResultPublished,
  type LabOrderRow,
  type LabOrderTestRow,
  type LabResultRow,
  type LabResultFileRow,
  type LaboratoryCapabilities,
} from '@helios/data-access';
import { usePowerSyncDb, usePowerSyncQuery } from '../../adapters/powersync';
import { useBrandTokens } from '../../shell/shared';
import { uploadToStorage } from '../../lib/upload-to-storage';
import { saveLabResultReady, type UploadedLabArtifact } from '../../services/laboratory-service';
import { LabStatusBadge, LabUnpublishedBadge } from './lab-shared';
import { FormRendererPage } from '../patient/encounters/FormRendererPage';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  test: LabOrderTestRow | null;
  order: LabOrderRow | null;
  capabilities: LaboratoryCapabilities;
}

export function LabResultDrawer({ open, onOpenChange, test, order, capabilities }: Props) {
  const brandTokens = useBrandTokens();
  const { db } = usePowerSyncDb();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [pending, setPending] = useState<File[]>([]);
  /** Draft form instances started in this session, linked on save. */
  const [startedForms, setStartedForms] = useState<Map<string, string>>(new Map());
  /** The form being filled in-place, if any. */
  const [openForm, setOpenForm] = useState<{ instanceId: string; name: string } | null>(null);
  const [summary, setSummary] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const actor = useMemo(
    () => ({ uid: user?.uid ?? '', name: user?.displayName ?? user?.email ?? null }),
    [user],
  );

  const { data: results = [] } = usePowerSyncQuery<LabResultRow>(
    qLabResults(test?.id ?? '__none__'),
  );
  const { data: artifactRows = [] } = usePowerSyncQuery<LabResultFileRow>(
    qLabResultFilesForTest(test?.id ?? '__none__'),
  );
  const { published, unpublished } = useMemo(
    () => partitionLabArtifacts(artifactRows),
    [artifactRows],
  );

  // Re-opening views the existing result instead of starting a duplicate.
  const draft = useMemo(() => results.find((r) => !isLabResultPublished(r)) ?? null, [results]);
  const hasResult = results.length > 0;

  // Published Lab Work forms assigned to THIS test's laboratory service — the
  // technician never searches the global catalog.
  const { data: assignedForms = [] } = usePowerSyncQuery<LabWorkFormRow>(
    test?.service_id ? qLabWorkFormsForService(test.service_id) : qNoOp('lab-work-forms'),
  );
  const presentation = useMemo(() => labResultFormsPresentation(assignedForms), [assignedForms]);

  // Structured responses already linked to the draft.
  const { data: linkedForms = [] } = usePowerSyncQuery<LabResultFormRow>(
    draft?.id ? qLabResultForms(draft.id) : qNoOp('lab-result-forms'),
  );

  useEffect(() => {
    if (!open) return;
    setPending([]);
    setStartedForms(new Map());
    setOpenForm(null);
    setSummary(draft?.summary ?? '');
    setError(null);
  }, [open, test?.id, draft?.summary]);

  const uploadVerdict = test ? canUploadLabResult({ status: test.status }) : { ok: false };
  // A structured form, an uploaded report, or both — any one is enough.
  const contentVerdict = canSaveLabResult({
    formInstanceIds: [...startedForms.values()],
    fileCount: pending.length,
    existingFileCount: published.length + unpublished.length,
    existingFormCount: linkedForms.length,
  });
  const canSave = capabilities.uploadResult && uploadVerdict.ok && contentVerdict.ok && !busy;

  const addFiles = useCallback((incoming: FileList | null) => {
    if (!incoming?.length) return;
    setPending((prev) => {
      const seen = new Set(prev.map((f) => `${f.name}|${f.size}|${f.lastModified}`));
      const next = [...prev];
      for (const f of Array.from(incoming)) {
        const key = `${f.name}|${f.size}|${f.lastModified}`;
        if (!seen.has(key)) {
          next.push(f);
          seen.add(key);
        }
      }
      return next;
    });
  }, []);

  /**
   * Begin (or resume) a structured response. Creates a draft `form_instances`
   * row through the existing Forms model — no encounter is required, which is
   * what lets a walk-in laboratory order carry a structured result.
   */
  const startForm = useCallback(
    async (form: LabWorkFormRow) => {
      if (!db || !test || !order) return;
      const existing = startedForms.get(form.id);
      if (existing) {
        setOpenForm({ instanceId: existing, name: form.name ?? 'Result form' });
        return;
      }
      const alreadyLinked = linkedForms.find((l) => l.form_definition_id === form.id);
      if (alreadyLinked?.form_instance_id) {
        setOpenForm({
          instanceId: alreadyLinked.form_instance_id,
          name: form.name ?? 'Result form',
        });
        return;
      }
      const instanceId = crypto.randomUUID();
      const now = new Date().toISOString();
      await db.execute(
        `INSERT INTO form_instances
          (id, org_id, clinic_id, form_definition_id, patient_id, encounter_id, appointment_id,
           queue_id, visit_id, status, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, NULL, NULL, ?, 'draft', ?, ?)`,
        [
          instanceId,
          order.org_id ?? '',
          order.clinic_id ?? null,
          form.id,
          order.patient_id ?? '',
          order.encounter_id ?? null,
          order.visit_id ?? null,
          now,
          now,
        ],
      );
      setStartedForms((prev) => new Map(prev).set(form.id, instanceId));
      setOpenForm({ instanceId, name: form.name ?? 'Result form' });
    },
    [db, test, order, startedForms, linkedForms],
  );

  const handleSave = useCallback(async () => {
    if (!db || !test || !order || !canSave) return;
    setBusy(true);
    setError(null);
    try {
      // Storage first, then one guarded service call does the rest.
      const artifacts: UploadedLabArtifact[] = [];
      for (const file of pending) {
        const fileId = crypto.randomUUID();
        const uploaded = await uploadToStorage({
          orgId: order.org_id ?? '',
          patientId: order.patient_id ?? '',
          file,
          fileId,
        });
        artifacts.push({
          fileId,
          filename: uploaded.filename,
          url: uploaded.downloadUrl,
          mimeType: uploaded.mimeType,
          sizeBytes: file.size,
          kind: file.type.startsWith('image/') ? 'image' : 'document',
        });
      }

      await saveLabResultReady({
        db,
        orgId: order.org_id ?? '',
        clinicId: order.clinic_id ?? null,
        patientId: order.patient_id ?? '',
        testId: test.id,
        resultId: draft?.id ?? null,
        encounterId: order.encounter_id ?? null,
        visitId: order.visit_id ?? null,
        artifacts,
        formInstanceIds: [...startedForms.values()],
        summary: summary.trim() || null,
        actor,
      });

      onOpenChange(false);
    } catch (err) {
      console.error('[LabResultDrawer] save failed:', err);
      // The test stays where it was — nothing advanced.
      setError(err instanceof Error ? err.message : 'The result could not be saved.');
    } finally {
      setBusy(false);
    }
  }, [db, test, order, canSave, pending, draft, summary, actor, startedForms, onOpenChange]);

  if (!test || !order) return null;

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={hasResult ? 'Laboratory Result' : 'Add Laboratory Result'}
      description={test.test_name ?? undefined}
      width={560}
      portalStyle={brandTokens}
      badges={<LabStatusBadge status={test.status} />}
      footer={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--sp-3)',
          }}
        >
          <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
            {pending.length === 0
              ? hasResult
                ? 'Add more files, or close to keep the result as it is.'
                : presentation === 'none'
                  ? 'Attach at least one report file.'
                  : 'Fill a result form or attach a report.'
              : `${pending.length} file${pending.length === 1 ? '' : 's'} ready to save`}
          </span>
          <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
            <Button variant="outline" size="md" onClick={() => onOpenChange(false)}>
              {/* Cancelling never touches the test's status. */}
              {hasResult ? 'Close' : 'Cancel'}
            </Button>
            <Button variant="gradient" size="md" onClick={handleSave} disabled={!canSave}>
              {busy ? 'Saving…' : 'Save Result'}
            </Button>
          </div>
        </div>
      }
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-4)',
          padding: 'var(--sp-5) var(--sp-6)',
        }}
      >
        {error && (
          <div
            role="alert"
            style={{
              padding: 'var(--sp-3)',
              borderRadius: 'var(--radius-md)',
              background: '#fee2e2',
              color: '#991b1b',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            {error}
          </div>
        )}

        {!uploadVerdict.ok && (
          <div
            style={{
              padding: 'var(--sp-3)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--bg-surface-2)',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            {('reason' in uploadVerdict && uploadVerdict.reason) ||
              'This test can no longer take a new result.'}
          </div>
        )}

        {/* ── Structured Lab Work forms assigned to this test's service ──
            The section renders even with nothing assigned: a technician who
            expects a form needs to see that none exists for this service,
            rather than a drawer that silently jumps to the file upload and
            leaves them wondering whether the form failed to load. */}
        {uploadVerdict.ok && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.6px',
              }}
            >
              {presentation === 'multiple' ? 'Result forms' : 'Result form'}
            </span>
            {assignedForms.map((f) => {
              const started =
                startedForms.has(f.id) || linkedForms.some((l) => l.form_definition_id === f.id);
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => startForm(f)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 'var(--sp-3)',
                    width: '100%',
                    textAlign: 'left',
                    padding: 'var(--sp-3)',
                    borderRadius: 'var(--radius-md)',
                    border: `1px solid ${started ? 'var(--color-primary)' : 'var(--border)'}`,
                    background: started ? 'var(--bg-surface-2)' : 'var(--bg-surface)',
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ minWidth: 0 }}>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'var(--font-weight-medium)',
                        color: 'var(--text)',
                      }}
                    >
                      {f.name}
                    </span>
                    {f.description && (
                      <span
                        style={{
                          display: 'block',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--text-muted)',
                        }}
                      >
                        {f.description}
                      </span>
                    )}
                  </span>
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-primary)' }}>
                    {started ? 'Continue' : 'Fill'}
                  </span>
                </button>
              );
            })}
            {presentation === 'none' ? (
              <div
                style={{
                  padding: 'var(--sp-3)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border)',
                  background: 'var(--bg-surface-2)',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text-muted)',
                  lineHeight: 1.5,
                }}
              >
                No forms are associated with this service. Upload a report below instead, or assign
                a Lab Work form to this service from Forms.
              </div>
            ) : (
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text-muted)',
                  lineHeight: 1.5,
                }}
              >
                Complete whichever apply — none of these are required, and you can upload a report
                instead of, or as well as, filling a form.
              </span>
            )}
          </div>
        )}

        {/* In-place form entry. Reuses the existing Forms renderer, so field
            values live in `form_instance_values` and are never copied into
            laboratory columns. */}
        {openForm && (
          <div
            style={{
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--sp-2)',
                padding: 'var(--sp-2) var(--sp-3)',
                borderBottom: '1px solid var(--border)',
                background: 'var(--bg-surface-2)',
              }}
            >
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text)',
                }}
              >
                {openForm.name}
              </span>
              <button
                type="button"
                onClick={() => setOpenForm(null)}
                style={{
                  border: 'none',
                  background: 'none',
                  color: 'var(--color-primary)',
                  fontSize: 'var(--font-size-sm)',
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                }}
              >
                Done
              </button>
            </div>
            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
              <FormRendererPage
                embedded
                overrideIds={{
                  patientId: order.patient_id ?? '',
                  encounterId: order.encounter_id ?? '',
                  formInstanceId: openForm.instanceId,
                }}
              />
            </div>
          </div>
        )}

        {/* Structured responses already saved on this result. */}
        {linkedForms.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.6px',
              }}
            >
              Saved result forms
            </span>
            {linkedForms.map((l) => (
              <button
                key={l.id}
                type="button"
                onClick={() =>
                  l.form_instance_id &&
                  setOpenForm({
                    instanceId: l.form_instance_id,
                    name: l.form_name ?? 'Result form',
                  })
                }
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 'var(--sp-2)',
                  padding: 'var(--sp-2) var(--sp-3)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border)',
                  background: 'var(--bg-surface)',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text)',
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <span>{l.form_name ?? 'Result form'}</span>
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                  {l.published_at ? 'Published' : (l.instance_status ?? 'draft')}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Existing artifacts — what makes re-opening a view, not a duplicate. */}
        {(published.length > 0 || unpublished.length > 0) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.6px',
              }}
            >
              Saved report files
            </span>
            {[...published, ...unpublished].map((a) => (
              <a
                key={a.linkId}
                href={a.url ?? '#'}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 'var(--sp-2)',
                  padding: 'var(--sp-2) var(--sp-3)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border)',
                  background: 'var(--bg-surface)',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text)',
                  textDecoration: 'none',
                }}
              >
                <span
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {a.filename}
                </span>
                {!a.published && <LabUnpublishedBadge />}
              </a>
            ))}
          </div>
        )}

        {uploadVerdict.ok && (
          <>
            <div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                style={{
                  width: '100%',
                  padding: 'var(--sp-6)',
                  borderRadius: 'var(--radius-lg)',
                  border: '2px dashed var(--border)',
                  background: 'transparent',
                  color: 'var(--text-muted)',
                  fontSize: 'var(--font-size-sm)',
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                }}
              >
                Click to choose report file(s) — PDF, image or document
              </button>
            </div>

            {pending.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
                {pending.map((f, i) => (
                  <div
                    key={`${f.name}-${i}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 'var(--sp-2)',
                      padding: 'var(--sp-2) var(--sp-3)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--border)',
                      fontSize: 'var(--font-size-sm)',
                    }}
                  >
                    <span
                      style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {f.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPending((prev) => prev.filter((_, idx) => idx !== i))}
                      aria-label={`Remove ${f.name}`}
                      style={{
                        border: 'none',
                        background: 'none',
                        color: 'var(--text-muted)',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--text)',
                  marginBottom: 'var(--sp-2)',
                }}
              >
                Summary <span style={{ color: 'var(--text-muted)' }}>(optional)</span>
              </label>
              <textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                rows={3}
                placeholder="Interpretation or notes for the clinician"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: 'var(--font-size-sm)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border)',
                  outline: 'none',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit',
                }}
              />
            </div>

            <span
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-muted)',
                lineHeight: 1.5,
              }}
            >
              Saving records the result and marks this test Result Ready. It is not yet visible to
              clinicians — publishing is a separate, explicit step.
            </span>
          </>
        )}
      </div>
    </Drawer>
  );
}

export default LabResultDrawer;
